/**
 * Performance monitoring utilities for development
 * These should be disabled in production builds
 */

import { isDevMode } from '@angular/core';

/**
 * Measures execution time of a function
 * Only active in development mode
 */
export function measurePerformance<T extends (...args: any[]) => any>(
  fn: T,
  label: string = fn.name || 'Anonymous'
): T {
  if (!isDevMode()) {
    return fn;
  }

  return ((...args: Parameters<T>): ReturnType<T> => {
    const start = performance.now();
    const result = fn(...args);
    const end = performance.now();
    
    if (end - start > 16) { // Log if execution takes more than one frame (16ms)
      console.warn(`[Performance] ${label} took ${(end - start).toFixed(2)}ms`);
    }
    
    return result;
  }) as T;
}

/**
 * Debounce function calls for performance
 * Useful for search inputs and resize handlers
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Throttle function calls for performance
 * Ensures function is called at most once per interval
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  interval: number = 100
): T {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return ((...args: Parameters<T>): ReturnType<T> | undefined => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;
    
    if (timeSinceLastCall >= interval) {
      lastCall = now;
      return fn(...args);
    }
    
    // Schedule a call for the remaining time
    if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        fn(...args);
        timeoutId = null;
      }, interval - timeSinceLastCall);
    }
    
    return undefined;
  }) as T;
}

/**
 * Request idle callback wrapper with fallback
 * Schedules low-priority work when browser is idle
 */
export function scheduleIdleWork(
  callback: () => void,
  timeout: number = 1000
): void {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(callback, { timeout });
  } else {
    // Fallback to setTimeout for browsers without requestIdleCallback
    setTimeout(callback, 0);
  }
}

/**
 * Batch updates to reduce reflows/repaints
 */
export class BatchUpdater {
  private updates: (() => void)[] = [];
  private scheduled = false;
  
  add(update: () => void): void {
    this.updates.push(update);
    
    if (!this.scheduled) {
      this.scheduled = true;
      requestAnimationFrame(() => this.flush());
    }
  }
  
  flush(): void {
    const updates = this.updates;
    this.updates = [];
    this.scheduled = false;
    
    // Execute all updates in a single frame
    updates.forEach(update => update());
  }
}